import {
  buildQueryExpression,
  buildStaticFilterValue,
  definePager,
  defineQuerySummary,
  defineResultList,
  defineResultsPerPage,
  defineSearchBox,
  defineSearchEngine,
  defineStaticFilter,
  type PagerDefinition,
  type QuerySummaryDefinition,
  type ResultListDefinition,
  type ResultsPerPageDefinition,
  type SearchBoxDefinition,
  type SearchEngineDefinitionOptions,
  type StaticFilterDefinition,
} from '@coveo/headless-react/ssr';
import { DEFAULT_LOCALE } from '@/i18n';
import { getServerEnv } from '../constants/environment';
import { renewSearchToken } from './token-manager';
import { SearchHub } from './types';

export const NUMBER_OF_RESULTS = 12;
const INTERNAL_DOCS_SOURCE = 'internal-docs';
const numberOfSuggestions = 10;

/**
 * Get static filter controllers for the search tabs.
 * Conditionally includes the 'internal' filter only for internal users.
 * @param searchHub The search hub context (internal or external)
 * @returns Array of static filter values
 * @see {@link https://docs.coveo.com/en/headless-react/latest/reference/interfaces/SSR_Search.StaticFilter.html}
 */
function getStaticFilterValues(searchHub: SearchHub) {
  const baseFilters = [
    buildStaticFilterValue({
      caption: 'all',
      state: 'idle',
      expression: buildQueryExpression()
        .addStringField({
          field: 'clickableuri',
          operator: 'contains',
          values: [
            'https://issuetracker.unity3d.com',
            'https://support.unity.com',
            'https://docs.unity3d.com',
            'https://docs.unity.com',
            'https://docs-multiplayer.unity3d.com',
            'https://services.docs.unity.com',
            'https://discussions.unity.com',
            'https://learn.unity.com',
          ],
        })
        .addStringField({
          field: 'source',
          operator: 'isExactly',
          values: [INTERNAL_DOCS_SOURCE],
        })
        .joinUsing('or')
        .toQuerySyntax(),
    }),
  ];

  // Only include the internal filter for internal users
  if (searchHub === SearchHub.internal) {
    baseFilters.push(
      buildStaticFilterValue({
        caption: 'internal',
        state: 'idle',
        expression: buildQueryExpression()
          .addStringField({
            field: 'source',
            operator: 'isExactly',
            values: [INTERNAL_DOCS_SOURCE],
          })
          .toQuerySyntax(),
      }),
    );
  }

  baseFilters.push(
    buildStaticFilterValue({
      caption: 'documentation',
      state: 'selected',
      expression: buildQueryExpression()
        .addStringField({
          field: 'clickableuri',
          operator: 'contains',
          values: [
            'https://docs.unity3d.com',
            'https://docs.unity.com',
            'https://docs-multiplayer.unity3d.com',
            'https://services.docs.unity.com',
          ],
        })
        .toQuerySyntax(),
    }),
    buildStaticFilterValue({
      caption: 'support',
      state: 'idle',
      expression: buildQueryExpression()
        .addStringField({
          field: 'clickableuri',
          operator: 'contains',
          values: ['https://support.unity.com'],
        })
        .toQuerySyntax(),
    }),
    buildStaticFilterValue({
      caption: 'learn',
      state: 'idle',
      expression: buildQueryExpression()
        .addStringField({
          field: 'clickableuri',
          operator: 'contains',
          values: ['https://learn.unity.com'],
        })
        .toQuerySyntax(),
    }),
    buildStaticFilterValue({
      caption: 'discussions',
      state: 'idle',
      expression: buildQueryExpression()
        .addStringField({
          field: 'clickableuri',
          operator: 'contains',
          values: ['https://discussions.unity.com'],
        })
        .toQuerySyntax(),
    }),
  );

  return baseFilters;
}

/**
 * Get the engine configuration options based on the search hub context.
 * @param searchHub The search hub context (internal or external)
 * @returns Search engine definition options with appropriate static filters
 */
export function getEngineConfig(searchHub: SearchHub): SearchEngineDefinitionOptions<{
  searchBox: SearchBoxDefinition;
  resultList: ResultListDefinition;
  pager: PagerDefinition;
  resultsPerPage: ResultsPerPageDefinition;
  querySummary: QuerySummaryDefinition;
  staticFilter: StaticFilterDefinition;
}> {
  return {
    configuration: {
      organizationId: getServerEnv().NEXT_PUBLIC_COVEO_ORG_ID || '',
      accessToken: '', // will be set in build functions
      renewAccessToken: renewSearchToken,
      search: {
        locale: DEFAULT_LOCALE,
        searchHub,
      },
      analytics: { enabled: true },
    },
    controllers: {
      searchBox: defineSearchBox({ options: { numberOfSuggestions, clearFilters: false } }),
      resultList: defineResultList(),
      pager: definePager(),
      resultsPerPage: defineResultsPerPage({ initialState: { numberOfResults: NUMBER_OF_RESULTS } }),
      querySummary: defineQuerySummary(),
      staticFilter: defineStaticFilter({
        options: {
          id: 'search-tabs',
          values: getStaticFilterValues(searchHub),
        },
      }),
    },
    loggerOptions: {
      level: getServerEnv().NODE_ENV === 'development' ? 'warn' : 'error',
    },
  };
}

/**
 * Default configuration for the Coveo search engine and it's controllers
 * @see {@link https://docs.coveo.com/en/headless/latest/usage/#configure-a-headless-engine}
 * @remarks
 * accessToken, locale, renewAccessToken, search.searchHub will all be overridden
 * by the engine build functions because they require different values for
 * searching internal (secure) and external (anonymous) documentation.
 */
const defaultConfig = getEngineConfig(SearchHub.external);
export const engineDefinition = defineSearchEngine(defaultConfig);
